import {
  Vector3,
  Mesh,
  BufferGeometry,
  Geometry,
  SphereGeometry,
  SphereBufferGeometry,
  MeshLambertMaterial,
  ArrowHelper,
  Euler,
  CylinderGeometry,
  CylinderBufferGeometry,
  Group,
  Quaternion
} from 'three'

import 'babel-polyfill'

import Noise from './Noise'
const noiseGenerator = new Noise(Math.random())

/**
 * Returns the centroid of a triangle.
 * The 3 vectors that are passed to this function,
 * should be the triangle's vertices.
 *
 * @param {Vector3} vec1
 * @param {Vector3} vec2
 * @param {Vector3} vec3
 * @return {Vector3}
 */
function getTriangleCentroid (vec1, vec2, vec3) {
  var centerX = ((vec1.x + vec2.x + vec3.x) / 3)
  var centerY = ((vec1.y + vec2.y + vec3.y) / 3)
  var centerZ = ((vec1.z + vec2.z + vec3.z) / 3)
  return new Vector3(centerX, centerY, centerZ)
}

/**
 * Gets a geometry's edges, as an a array of arrays.
 * Each
 */
export function getGeometryEdges (geometry) {
  var edges = []
  var edgesStrings = []

  geometry.faces.forEach(function (face, i) {
    if (!(edgesStrings.includes(face.a + ' ' + face.b) || edgesStrings.includes(face.b + ' ' + face.a))) {
      edges.push([face.a, face.b])
      edgesStrings.push(face.a + ' ' + face.b)
    }

    if (!(edgesStrings.includes(face.b + ' ' + face.c) || edgesStrings.includes(face.c + ' ' + face.b))) {
      edges.push([face.b, face.c])
      edgesStrings.push(face.b + ' ' + face.c)
    }

    if (!(edgesStrings.includes(face.c + ' ' + face.a) || edgesStrings.includes(face.a + ' ' + face.c))) {
      edges.push([face.c, face.a])
      edgesStrings.push(face.c + ' ' + face.a)
    }
  }, this)

  return edges
}

/**
 *
 * @param {*} mesh
 */
export function storeOriginalVertices (mesh) {
  mesh.userData.originalVertices = []
  mesh.geometry.vertices.forEach((vertex, i) => { mesh.userData.originalVertices[i] = vertex.clone() })
}

/**
 *
 * @param {*} mesh
 */
export function storeOriginalCentroids (mesh) {
  let geometry = mesh.geometry
  mesh.userData.originalCentroids = []

  geometry.faces.forEach((face, i) => {
    mesh.userData.originalCentroids[i] = getTriangleCentroid(
      geometry.vertices[face.a],
      geometry.vertices[face.b],
      geometry.vertices[face.c]
    )
  })
}

/**
 * Move the vertices by using perlin noise.
 */
export function turbulateVertices (geometry, amount) {
  geometry.vertices.forEach(function (vertex) {
    vertex.x += noiseGenerator.perlin2(vertex.y, vertex.z) * amount
    vertex.y += noiseGenerator.perlin2(vertex.x, vertex.z) * amount
    vertex.z += noiseGenerator.perlin2(vertex.x, vertex.y) * amount
  })

  geometry.verticesNeedUpdate = true
}

export function addSpheresToVerticesLite (mesh) {
  let mergedGeometry = mesh.geometry.vertices.reduce(function (accumulator, vertex, index) {
    let vertexGeometry = new SphereGeometry(0.1, 4, 4)
    vertexGeometry.translate(vertex.x, vertex.y, vertex.z)
    accumulator.merge(vertexGeometry)
    vertexGeometry.dispose()
    return accumulator
  }, new Geometry())

  return mergedGeometry
}

/**
 *
 */
function createCylinderMesh (point1, point2, material, buffer = true) {
  var direction = new Vector3().subVectors(point2, point1)
  var arrow = new ArrowHelper(direction.clone().normalize(), point1)

  var rotation = new Euler().setFromQuaternion(arrow.quaternion)
  var length = direction.length()

  var edgeGeometry = buffer ? new CylinderBufferGeometry(0.025, 0.025, length, 8, 1) : new CylinderGeometry(0.025, 0.025, length, 8, 1)
  var edge = new Mesh(edgeGeometry, material)

  var pos = new Vector3().addVectors(point1, direction.multiplyScalar(0.5))

  edge.userData = {
    point1: point1,
    point2: point2,
    originalLength: length
  }

  edge.position.x = pos.x
  edge.position.y = pos.y
  edge.position.z = pos.z
  edge.rotation.x = rotation.x
  edge.rotation.y = rotation.y
  edge.rotation.z = rotation.z

  edge.updateMatrix()

  return edge
}

/**
 *
 */
export function addCylindersToEdgesLite (mesh) {
  let edges = getGeometryEdges(mesh.geometry)
  let geometry = mesh.geometry
  let mergedGeometry = new Geometry()
  let edgeMaterial = new MeshLambertMaterial({ color: 0x1a2140, transparent: true })
  edges.forEach(function (edge) {
    let cylinderMesh = createCylinderMesh(geometry.vertices[edge[0]], geometry.vertices[edge[1]], edgeMaterial, false)
    mergedGeometry.mergeMesh(cylinderMesh)
    cylinderMesh.geometry.dispose()
  })

  return mergedGeometry
}

/**
 * Explodes a geometry. AKA distances the faces of the geometry
 * for the geometry's center
 *
 * @param {THREE.Mesh} mesh
 * @param {number} distance The total distance we want to cover
 * @param {number} amount The amount of the total distance that this call to the function should go to
 */
export function explodeGeometry (mesh, distance, amount) {
  let geometry = mesh.geometry
  geometry.faces.forEach(function (face, i) {
    let centroid = mesh.userData.originalCentroids[i]
    let directionAxis = centroid.normalize().multiplyScalar(distance * amount)
    let faceArray = [face.a, face.b, face.c]

    faceArray.forEach(function (vertexIndex) {
      let newPos = new Vector3().addVectors(mesh.userData.originalVertices[vertexIndex], directionAxis)
      geometry.vertices[vertexIndex].copy(newPos)
    })
  })

  geometry.verticesNeedUpdate = true
}

/**
 * Clear a timeline, but run its onComplete callback first.
 * @param {object} timeline
 */
export function safeClearTimeline (timeline) {
  if (timeline && timeline.isActive()) {
    const onCompleteCallback = timeline.eventCallback('onComplete')
    if (onCompleteCallback) {
      onCompleteCallback()
    }
    timeline.clear()
    timeline.progress(1)
  }
}



// WEBPACK FOOTER //
// ./src/lib/3dUtils.js